/**
 * Redux slice (src/store/slices/counsellorSlice.js).
 *
 * Data flow (typical):
 * - UI dispatches actions/thunks from `src/store/actions/*`.
 * - `reducers` handles synchronous state updates.
 * - `extraReducers` reacts to thunk pending/fulfilled/rejected actions.
 */

import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { t } from "../../utils/i18nTranslate";
import {
  loginCounsellor,
  getCounsellorProfile,
  updateCounsellorProfile,
  changeCounsellorPassword,
  logoutCounsellor,
  createInvite,
  getInvitesList,
  getAllCounsellors,
  createCounsellorByAdmin,
  updateCounsellorByAdmin,
  deleteCounsellorByAdmin,
  getCounsellorTeachers,
  getCounsellorTeacherDetails,
  getCounsellorCourses,
  getCounsellorPackages
} from "../actions/counsellorActions";

/**
 * Slice initial state.
 *
 * Keep this shape stable because it is consumed across many selectors/components.
 */
const initialState = {
  counsellor: null,
  counsellors: [],
  invites: [],
  invitesSummary: null,
  teachers: [],
  selectedTeacher: null,
  courses: [],
  packages: [],
  loading: false,
  isAuthenticated: false,
  error: null,
  loginError: null,
  invitesLoading: false,
  invitesError: null,
  teachersLoading: false,
  teachersError: null,
  teachersStatus: "idle",
  teachersRequestId: null,
  coursesLoading: false,
  coursesError: null,
  packagesLoading: false,
  packagesError: null,
  passwordLoading: false,
  passwordError: null,
  passwordSuccess: null,
};

/**
 * RTK slice definition: counsellorSlice.
 */
const counsellorSlice = createSlice({
  name: "counsellor",
  initialState,
  reducers: {
    /**
     * Reducer: clearCounsellor.
     */
    clearCounsellor: (state) => {
      state.counsellor = null;
      state.isAuthenticated = false;
      state.loginError = null;
    },
    /**
     * Reducer: clearError.
     */
    clearError: (state) => {
      state.error = null;
      state.loginError = null;
    },
  },
  /**
   * Reducer: extraReducers.
   */
  extraReducers: (builder) => {
    builder
      // Login Counsellor
      .addCase(loginCounsellor.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.loginError = null;
      })
      // extraReducers handler: loginCounsellor.fulfilled
      .addCase(loginCounsellor.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.counsellor = action.payload;
        state.loginError = null;
        toast.success(t("store.counsellor.loginSuccessful"));
      })
      // extraReducers handler: loginCounsellor.rejected
      .addCase(loginCounsellor.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.loginError = action.payload;
        toast.error(action.payload || t("store.counsellor.loginFailed"));
      })

      // Get Counsellor Profile
      .addCase(getCounsellorProfile.pending, (state) => {
        state.loading = true;
        state.loginError = null;
      })
      // extraReducers handler: getCounsellorProfile.fulfilled
      .addCase(getCounsellorProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.counsellor = action.payload;
      })
      // extraReducers handler: getCounsellorProfile.rejected
      .addCase(getCounsellorProfile.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.error = action.payload;
      })

      // Update Counsellor Profile
      .addCase(updateCounsellorProfile.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: updateCounsellorProfile.fulfilled
      .addCase(updateCounsellorProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.counsellor = action.payload;
        toast.success(t("store.counsellor.profileUpdated"));
      })
      // extraReducers handler: updateCounsellorProfile.rejected
      .addCase(updateCounsellorProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        toast.error(action.payload || t("store.counsellor.updateFailed"));
      })

      // Change Counsellor Password
      .addCase(changeCounsellorPassword.pending, (state) => {
        state.passwordLoading = true;
        state.passwordError = null;
        state.passwordSuccess = null;
      })
      .addCase(changeCounsellorPassword.fulfilled, (state, action) => {
        state.passwordLoading = false;
        state.passwordSuccess = true;
        state.passwordError = null;
        state.counsellor = action.payload.counsellor;
        state.isAuthenticated = true;
      })
      .addCase(changeCounsellorPassword.rejected, (state, action) => {
        state.passwordLoading = false;
        state.passwordError = action.payload;
      })

      // Logout Counsellor
      .addCase(logoutCounsellor.fulfilled, (state) => {
        state.counsellor = null;
        state.isAuthenticated = false;
        toast.success(t("store.counsellor.loggedOut"));
      })

      // Create Invite
      .addCase(createInvite.pending, (state) => {
        state.invitesLoading = true;
        state.invitesError = null;
      })
      // extraReducers handler: createInvite.fulfilled
      .addCase(createInvite.fulfilled, (state, action) => {
        state.invitesLoading = false;
        state.invites.unshift(action.payload);
        toast.success(t("store.counsellor.inviteCreated"));
      })
      // extraReducers handler: createInvite.rejected
      .addCase(createInvite.rejected, (state, action) => {
        state.invitesLoading = false;
        state.invitesError = action.payload;
        toast.error(action.payload || t("store.counsellor.inviteCreateFailed"));
      })

      // Get Invites List
      .addCase(getInvitesList.pending, (state) => {
        state.invitesLoading = true;
        state.invitesError = null;
      })
      // extraReducers handler: getInvitesList.fulfilled
      .addCase(getInvitesList.fulfilled, (state, action) => {
        state.invitesLoading = false;
        state.invitesError = null;
        if (Array.isArray(action.payload)) {
          state.invites = action.payload;
          state.invitesSummary = null;
        } else {
          state.invites = Array.isArray(action.payload?.invites)
            ? action.payload.invites
            : [];
          state.invitesSummary = action.payload?.summary || null;
        }
      })
      // extraReducers handler: getInvitesList.rejected
      .addCase(getInvitesList.rejected, (state, action) => {
        state.invitesLoading = false;
        state.invitesError = action.payload;
      })

      // Admin: Get All Counsellors
      .addCase(getAllCounsellors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: getAllCounsellors.fulfilled
      .addCase(getAllCounsellors.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.counsellors = action.payload;
      })
      // extraReducers handler: getAllCounsellors.rejected
      .addCase(getAllCounsellors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Admin: Create Counsellor
      .addCase(createCounsellorByAdmin.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: createCounsellorByAdmin.fulfilled
      .addCase(createCounsellorByAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.counsellors.push(action.payload);
        toast.success(t("store.counsellor.createdSuccessfully"));
      })
      // extraReducers handler: createCounsellorByAdmin.rejected
      .addCase(createCounsellorByAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        toast.error(action.payload || t("store.counsellor.createFailed"));
      })

      // Admin: Update Counsellor
      .addCase(updateCounsellorByAdmin.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: updateCounsellorByAdmin.fulfilled
      .addCase(updateCounsellorByAdmin.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.counsellors.findIndex(c => c._id === action.payload._id);
        if (index !== -1) {
          state.counsellors[index] = action.payload;
        }
        toast.success(t("store.counsellor.updatedSuccessfully"));
      })
      // extraReducers handler: updateCounsellorByAdmin.rejected
      .addCase(updateCounsellorByAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        toast.error(action.payload || t("store.counsellor.updateCounsellorFailed"));
      })

      // Admin: Delete Counsellor
      .addCase(deleteCounsellorByAdmin.pending, (state) => {
        state.loading = true;
      })
      // extraReducers handler: deleteCounsellorByAdmin.fulfilled
      .addCase(deleteCounsellorByAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.counsellors = state.counsellors.filter(c => c._id !== action.payload);
        toast.success(t("store.counsellor.deletedSuccessfully"));
      })
      // extraReducers handler: deleteCounsellorByAdmin.rejected
      .addCase(deleteCounsellorByAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        toast.error(action.payload || t("store.counsellor.deleteCounsellorFailed"));
      })

      // Get Teachers
      .addCase(getCounsellorTeachers.pending, (state, action) => {
        state.teachersLoading = true;
        state.teachersStatus = "loading";
        state.teachersError = null;
        state.teachersRequestId = action.meta?.requestId || null;
      })
      // extraReducers handler: getCounsellorTeachers.fulfilled
      .addCase(getCounsellorTeachers.fulfilled, (state, action) => {
        if (state.teachersRequestId && action.meta?.requestId !== state.teachersRequestId) {
          return;
        }
        state.teachersLoading = false;
        state.teachersRequestId = null;
        if (!Array.isArray(action.payload?.teachers)) {
          state.teachersStatus = "error";
          state.teachersError = "Invalid teacher response shape.";
          return;
        }
        state.teachersStatus = "success";
        state.teachers = action.payload.teachers;
      })
      // extraReducers handler: getCounsellorTeachers.rejected
      .addCase(getCounsellorTeachers.rejected, (state, action) => {
        if (state.teachersRequestId && action.meta?.requestId !== state.teachersRequestId) {
          return;
        }
        state.teachersLoading = false;
        state.teachersStatus = "error";
        state.teachersRequestId = null;
        state.teachersError = action.payload || action.error?.message || t("store.counsellor.failedToFetchTeachers");
        toast.error(state.teachersError);
      })

      // Get Teacher Details
      .addCase(getCounsellorTeacherDetails.pending, (state) => {
        state.teachersLoading = true;
        state.teachersError = null;
      })
      // extraReducers handler: getCounsellorTeacherDetails.fulfilled
      .addCase(getCounsellorTeacherDetails.fulfilled, (state, action) => {
        state.teachersLoading = false;
        state.selectedTeacher = action.payload;
      })
      // extraReducers handler: getCounsellorTeacherDetails.rejected
      .addCase(getCounsellorTeacherDetails.rejected, (state, action) => {
        state.teachersLoading = false;
        state.teachersError = action.payload;
        toast.error(action.payload || t("store.counsellor.failedToFetchTeacherDetails"));
      })

      // Get Courses
      .addCase(getCounsellorCourses.pending, (state) => {
        state.coursesLoading = true;
        state.coursesError = null;
      })
      // extraReducers handler: getCounsellorCourses.fulfilled
      .addCase(getCounsellorCourses.fulfilled, (state, action) => {
        state.coursesLoading = false;
        state.courses = action.payload.courses;
      })
      // extraReducers handler: getCounsellorCourses.rejected
      .addCase(getCounsellorCourses.rejected, (state, action) => {
        state.coursesLoading = false;
        state.coursesError = action.payload;
        toast.error(action.payload || t("store.counsellor.failedToFetchCourses"));
      })

      // Get Packages
      .addCase(getCounsellorPackages.pending, (state) => {
        state.packagesLoading = true;
        state.packagesError = null;
      })
      // extraReducers handler: getCounsellorPackages.fulfilled
      .addCase(getCounsellorPackages.fulfilled, (state, action) => {
        state.packagesLoading = false;
        state.packages = action.payload.packages;
      })
      // extraReducers handler: getCounsellorPackages.rejected
      .addCase(getCounsellorPackages.rejected, (state, action) => {
        state.packagesLoading = false;
        state.packagesError = action.payload;
        toast.error(action.payload || t("store.counsellor.failedToFetchPackages"));
      });
  },
});

export const { clearCounsellor, clearError } = counsellorSlice.actions;
export default counsellorSlice.reducer;
